import {
  httpCreateCardDispute,
  httpRequestCardDisputeFee,
  httpUpdateCardDispute,
} from "api/admin/virtual-card";
import useAdminAccess from "hooks/useAdminAccess";
import { useState } from "react";
import { useQueryClient } from "react-query";
import { showDanger, showSuccess } from "utilities/misc";
import {
  Button,
  Drawer,
  ReasonDialog,
  errorMessage,
  fieldClass,
  formatLabel,
} from "../access-control/components";
export default function CardDisputeAction({
  transactionId,
  dispute,
  cardId,
}: {
  transactionId?: string;
  dispute?: { id: number; status: string };
  cardId: string;
}) {
  const { can } = useAdminAccess();
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(dispute?.status || "open");
  const permitted = can(
    dispute ? "virtualCard.disputes.approve" : "virtualCard.disputes.create",
  );
  const confirm = async (reason: string) => {
    if (busy || !permitted) return;
    setBusy(true);
    try {
      if (dispute) await httpUpdateCardDispute(dispute.id, status, reason);
      else if (transactionId)
        await httpCreateCardDispute(transactionId, reason);
      else return;
      setOpen(false);
      showSuccess(dispute ? "Dispute status updated" : "Dispute created");
      await client.invalidateQueries(["virtualCardDisputes", cardId]);
    } catch (error) {
      showDanger(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };
  if (!permitted)
    return dispute ? <DisputeFeeAction disputeId={dispute.id} /> : null;
  return (
    <div className="min-w-[160px] space-y-2">
      {dispute && (
        <select
          aria-label={`Status for dispute ${dispute.id}`}
          className={fieldClass}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {["open", "in_review", "resolved", "rejected"].map((v) => (
            <option key={v} value={v}>
              {formatLabel(v)}
            </option>
          ))}
        </select>
      )}
      <Button
        variant="secondary"
        disabled={
          busy || (dispute ? status === dispute.status : !transactionId)
        }
        onClick={() => setOpen(true)}
      >
        {dispute ? "Update status" : "Raise dispute"}
      </Button>
      <ReasonDialog
        open={open}
        title={dispute ? "Update dispute status" : "Raise card dispute"}
        description={
          dispute
            ? `Change this dispute to ${formatLabel(status)}. This records the review outcome; it does not refund the transaction.`
            : "Describe why this transaction is disputed. This records a dispute for review."
        }
        confirmLabel={dispute ? "Update status" : "Create dispute"}
        busy={busy}
        close={() => !busy && setOpen(false)}
        confirm={confirm}
      />
      {dispute && <DisputeFeeAction disputeId={dispute.id} />}
    </div>
  );
}

function DisputeFeeAction({ disputeId }: { disputeId: number }) {
  const { can } = useAdminAccess();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reference, setReference] = useState("");
  const [reason, setReason] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  if (!can("virtualCard.disputes.create")) return null;
  const ready = reference.trim() && reason.trim() && acknowledged;
  return (
    <>
      <Button
        variant="secondary"
        disabled={submitted}
        onClick={() => setOpen(true)}
      >
        {submitted ? "Fee submitted for approval" : "Request $50 dispute fee"}
      </Button>
      <Drawer
        open={open}
        title="Request $50 dispute fee"
        description="Use only after formally submitting the USD-card dispute to the provider and obtaining the customer's fee acknowledgement. A second admin must approve collection."
        close={() => !busy && setOpen(false)}
      >
        <form
          className="space-y-5"
          onSubmit={async (event) => {
            event.preventDefault();
            if (!ready || busy) return;
            setBusy(true);
            try {
              await httpRequestCardDisputeFee(
                disputeId,
                reference.trim(),
                reason.trim(),
              );
              setSubmitted(true);
              setOpen(false);
              showSuccess(
                "$50 dispute fee submitted for second-admin approval",
              );
            } catch (error) {
              showDanger(errorMessage(error));
            } finally {
              setBusy(false);
            }
          }}
        >
          <label className="block text-sm">
            Provider dispute reference
            <input
              required
              maxLength={200}
              className={fieldClass}
              value={reference}
              onChange={(event) => setReference(event.target.value)}
            />
          </label>
          <label className="block text-sm">
            Reason
            <textarea
              required
              className={fieldClass}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
            />
            The customer has acknowledged the $50 dispute fee.
          </label>
          <Button type="submit" disabled={!ready || busy}>
            {busy ? "Submitting..." : "Submit for approval"}
          </Button>
        </form>
      </Drawer>
    </>
  );
}
